import { Component } from '@theme/component';
import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { addToCart, visibleSectionIds } from '@theme/cart-utils';

/**
 * AJAX add-to-cart button. Adds `data-variant-id` to the cart via the cart-utils
 * helper (no product form required, unlike the form-based <add-to-cart-component>)
 * and dispatches the theme's cart events. Parents can call `setVariant()` to sync
 * it with a variant selector. Shows a spinner while adding and a check on success.
 */
class AddToCartButtonAjax extends Component {
  #submitting = false;

  /**
   * @param {string|number} id
   * @param {boolean} [available]
   */
  setVariant(id, available) {
    if (id != null) this.dataset.variantId = String(id);
    if (this.refs.button) this.refs.button.disabled = available === false;
  }

  /** @param {Event} [event] */
  async add(event) {
    event?.preventDefault();
    if (this.#submitting) return;

    const variantId = this.dataset.variantId;
    if (!variantId) {
      // No variant chosen yet — let the parent (mini card) surface an error.
      this.dispatchEvent(new CustomEvent('add-to-cart-ajax:no-variant', { bubbles: true }));
      return;
    }

    this.#submitting = true;
    this.#showState('loading', this.dataset.statusAdding);

    try {
      const response = await addToCart(variantId, {
        sections: visibleSectionIds(),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        document.dispatchEvent(
          new CartErrorEvent(
            `add-to-cart-button-ajax-${variantId}`,
            err.message || this.dataset.statusError,
            err.description || '',
            err.errors || {},
          ),
        );
        this.#showState('idle', this.dataset.statusError);
        return;
      }

      const cart = await response.json();

      document.dispatchEvent(
        new CartAddEvent(cart, `add-to-cart-button-ajax-${variantId}`, {
          source: 'add-to-cart-button-ajax',
          itemCount: cart.item_count,
          variantId,
          sections: cart.sections,
        }),
      );

      this.#showState('success', this.dataset.statusAdded);
      setTimeout(() => this.#showState('idle', ''), 1500);
    } catch (error) {
      console.error('AJAX add-to-cart error:', error);
      this.#showState('idle', this.dataset.statusError);
    } finally {
      this.#submitting = false;
    }
  }

  /**
   * Set the visual state. The label/spinner/check are grid-stacked and toggled
   * via visibility (CSS keyed off data-state) so the button width never shifts.
   * @param {'idle'|'loading'|'success'} state
   * @param {string} [message] - Announced via the aria-live status region
   */
  #showState(state, message) {
    const { button, status } = this.refs;

    if (button) {
      button.dataset.state = state;
      const loading = state === 'loading';
      button.disabled = loading;
      button.toggleAttribute('aria-busy', loading);
    }
    if (status && message !== undefined) status.textContent = message || '';
  }
}

if (!customElements.get('add-to-cart-button-ajax')) {
  customElements.define('add-to-cart-button-ajax', AddToCartButtonAjax);
}
